import { goClient } from '../utils/httpClientTs';
import { goRequest } from '../utils/httpClientTs';
import type {
  CheckBatchDetailResponse,
  CheckBatchesListResponse,
  CheckPaymentDTO,
  CreateCheckBatchResponse,
  PaginatedMeta,
  PayPalActivityResponse,
  PayPalSettlementDetail,
  PayPalTransactionsResponse,
  PendingChecksBySourceResponse,
} from '../types/financePayments';

/** Base path for ELF Payments APIs (loan:manage). */
export const ELF_PAYMENTS_API_BASE = '/finance/elf-payments';

export const getElfPayPalTransactions = async (params: {
  page: number;
  page_size: number;
  unmatched?: boolean;
  has_error?: boolean;
  recurring?: boolean | null;
}): Promise<PayPalTransactionsResponse> => {
  const qs = new URLSearchParams({
    page: String(params.page),
    page_size: String(params.page_size),
  });
  if (params.unmatched) qs.set('unmatched', '1');
  if (params.has_error) qs.set('has_error', '1');
  if (params.recurring === true) qs.set('recurring', '1');
  if (params.recurring === false) qs.set('recurring', '0');
  return goRequest<PayPalTransactionsResponse>({
    url: `${ELF_PAYMENTS_API_BASE}/paypal/transactions?${qs.toString()}`,
    method: 'GET',
  });
};

export const getElfPayPalTransaction = async (
  id: string
): Promise<PayPalSettlementDetail> => {
  return goRequest<PayPalSettlementDetail>({
    url: `${ELF_PAYMENTS_API_BASE}/paypal/transactions/${encodeURIComponent(id)}`,
    method: 'GET',
  });
};

export const getElfPayPalActivity = async (params: {
  page: number;
  page_size: number;
  has_error?: boolean;
  recurring?: boolean | null;
}): Promise<PayPalActivityResponse> => {
  const qs = new URLSearchParams({
    page: String(params.page),
    page_size: String(params.page_size),
  });
  if (params.has_error) qs.set('has_error', '1');
  if (params.recurring === true) qs.set('recurring', '1');
  if (params.recurring === false) qs.set('recurring', '0');
  return goRequest<PayPalActivityResponse>({
    url: `${ELF_PAYMENTS_API_BASE}/paypal/activity?${qs.toString()}`,
    method: 'GET',
  });
};

export const getElfCheckPayments = async (params: {
  page: number;
  page_size: number;
  status?: string;
  unbatched_only?: boolean;
  check_number?: string;
  source?: string;
}): Promise<{ data: CheckPaymentDTO[]; pagination: PaginatedMeta }> => {
  const qs = new URLSearchParams({
    page: String(params.page),
    page_size: String(params.page_size),
  });
  if (params.status) qs.set('status', params.status);
  if (params.unbatched_only) qs.set('unbatched_only', '1');
  if (params.check_number?.trim())
    qs.set('check_number', params.check_number.trim());
  if (params.source?.trim()) qs.set('source', params.source.trim());
  return goRequest({
    url: `${ELF_PAYMENTS_API_BASE}/checks?${qs.toString()}`,
    method: 'GET',
  });
};

export const deleteElfCheckPayment = async (
  checkPaymentId: string
): Promise<void> => {
  await goRequest({
    url: `${ELF_PAYMENTS_API_BASE}/checks/${encodeURIComponent(checkPaymentId)}`,
    method: 'DELETE',
  });
};

export const getElfPendingChecksBySource =
  async (): Promise<PendingChecksBySourceResponse> => {
    return goRequest<PendingChecksBySourceResponse>({
      url: `${ELF_PAYMENTS_API_BASE}/checks/pending-by-source`,
      method: 'GET',
    });
  };

export const getElfCheckBatches = async (params: {
  page: number;
  page_size: number;
  status?: string;
}): Promise<CheckBatchesListResponse> => {
  const qs = new URLSearchParams({
    page: String(params.page),
    page_size: String(params.page_size),
    source: 'elf_loans',
  });
  if (params.status) qs.set('status', params.status);
  return goRequest<CheckBatchesListResponse>({
    url: `${ELF_PAYMENTS_API_BASE}/check-batches?${qs.toString()}`,
    method: 'GET',
  });
};

export const createElfCheckBatch = async (body?: {
  batch_number?: string;
}): Promise<CreateCheckBatchResponse> => {
  return goRequest<CreateCheckBatchResponse>({
    url: `${ELF_PAYMENTS_API_BASE}/check-batches`,
    method: 'POST',
    data: { ...(body ?? {}), source: 'elf_loans' },
  });
};

export const deleteElfCheckBatch = async (batchId: string): Promise<void> => {
  await goRequest({
    url: `${ELF_PAYMENTS_API_BASE}/check-batches/${encodeURIComponent(batchId)}`,
    method: 'DELETE',
  });
};

export const getElfCheckBatch = async (
  batchId: string
): Promise<CheckBatchDetailResponse> => {
  return goRequest<CheckBatchDetailResponse>({
    url: `${ELF_PAYMENTS_API_BASE}/check-batches/${encodeURIComponent(batchId)}`,
    method: 'GET',
  });
};

export const addElfChecksToBatch = async (
  batchId: string,
  checkPaymentIds: string[]
): Promise<void> => {
  await goRequest({
    url: `${ELF_PAYMENTS_API_BASE}/check-batches/${encodeURIComponent(batchId)}/checks`,
    method: 'POST',
    data: { check_payment_ids: checkPaymentIds },
  });
};

export const removeElfCheckFromBatch = async (
  batchId: string,
  checkPaymentId: string
): Promise<void> => {
  await goRequest({
    url: `${ELF_PAYMENTS_API_BASE}/check-batches/${encodeURIComponent(batchId)}/checks/${encodeURIComponent(checkPaymentId)}`,
    method: 'DELETE',
  });
};

export const finalizeElfCheckBatch = async (batchId: string): Promise<void> => {
  await goRequest({
    url: `${ELF_PAYMENTS_API_BASE}/check-batches/${encodeURIComponent(batchId)}/finalize`,
    method: 'POST',
  });
};

export const downloadElfCheckBatchPdf = async (
  batchId: string
): Promise<void> => {
  const res = await goClient.get(
    `${ELF_PAYMENTS_API_BASE}/check-batches/${encodeURIComponent(batchId)}/print`,
    { responseType: 'blob' }
  );
  const blob = new Blob([res.data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `check-batch-${batchId}.pdf`;
  a.click();
  window.URL.revokeObjectURL(url);
};

export const downloadElfCheckBatchDepositReport = async (
  batchId: string
): Promise<void> => {
  const res = await goClient.get(
    `${ELF_PAYMENTS_API_BASE}/check-batches/${encodeURIComponent(batchId)}/deposit-report`,
    { responseType: 'blob' }
  );
  const blob = new Blob([res.data], { type: 'application/pdf' });
  const href = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = `deposit-gl-check-batch-${batchId}.pdf`;
  a.click();
  window.URL.revokeObjectURL(href);
};
